import React from 'react'
import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import {
  FormatAlignCenterOutlined,
  FormatAlignJustifyOutlined,
  FormatAlignLeftOutlined,
  FormatAlignRightOutlined,
} from '@mui/icons-material'
import { IColorOption } from './PropertyConfigurationSection.action'
import { BoldRow, ColorSwatch, ColorSwatchRow, PropertyLabel, FONT_SIZES } from './PropertiesShared.styles'
import { TextAlignment } from 'src/types/report-designer'

// ============================================================================
// AlignmentToggleGroup
// ============================================================================

export interface IAlignmentToggleGroupProps {
  value: TextAlignment
  onChange: (_: React.MouseEvent<HTMLElement>, value: TextAlignment | null) => void
}

export const AlignmentToggleGroup: React.FC<IAlignmentToggleGroupProps> = ({ value, onChange }) => (
  <ToggleButtonGroup size="small" exclusive value={value} onChange={onChange}>
    <ToggleButton value={TextAlignment.Left}>
      <FormatAlignLeftOutlined fontSize="small" />
    </ToggleButton>
    <ToggleButton value={TextAlignment.Center}>
      <FormatAlignCenterOutlined fontSize="small" />
    </ToggleButton>
    <ToggleButton value={TextAlignment.Right}>
      <FormatAlignRightOutlined fontSize="small" />
    </ToggleButton>
    <ToggleButton value={TextAlignment.Justify}>
      <FormatAlignJustifyOutlined fontSize="small" />
    </ToggleButton>
  </ToggleButtonGroup>
)

// ============================================================================
// BoldToggleRow
// ============================================================================

export interface IBoldToggleRowProps {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}

export const BoldToggleRow: React.FC<IBoldToggleRowProps> = ({ label, checked, onChange }) => (
  <BoldRow>
    <PropertyLabel sx={{ mb: 0 }}>{label}</PropertyLabel>
    <Switch size="small" checked={checked} onChange={e => onChange(e.target.checked)} />
  </BoldRow>
)

// ============================================================================
// ColorSwatchField
// ============================================================================

export interface IColorSwatchFieldProps {
  colors: IColorOption[]
  selectedColor: string
  onColorChange: (color: string) => void
}

export const ColorSwatchField: React.FC<IColorSwatchFieldProps> = ({ colors, selectedColor, onColorChange }) => (
  <ColorSwatchRow>
    {colors.map(color => (
      <Tooltip key={color.value} title={color.label} placement="top" arrow>
        <ColorSwatch
          isSelected={selectedColor === color.value}
          swatchColor={color.value}
          onClick={() => onColorChange(color.value)}
        />
      </Tooltip>
    ))}
  </ColorSwatchRow>
)

// ============================================================================
// FontSizeSelect
// ============================================================================

export interface IFontSizeSelectProps {
  value: number
  onChange: (size: number) => void
  sizes?: number[]
}

export const FontSizeSelect: React.FC<IFontSizeSelectProps> = ({ value, onChange, sizes = FONT_SIZES }) => (
  <FormControl size="small" fullWidth>
    <Select<number> value={value} onChange={(e: SelectChangeEvent<number>) => onChange(Number(e.target.value))}>
      {sizes.map(size => (
        <MenuItem key={size} value={size}>
          {size}px
        </MenuItem>
      ))}
    </Select>
  </FormControl>
)

// ============================================================================
// FontFamilySelect
// ============================================================================

export interface IFontFamilySelectProps {
  value: string
  onChange: (family: string) => void
}

const FONT_FAMILIES = [
  'Arial',
  'Verdana',
  'Times New Roman',
  'Courier New',
  'Georgia',
  'Trebuchet MS',
  'Palatino',
  'Garamond',
  'Bookman',
  'Comic Sans MS',
]

export const FontFamilySelect: React.FC<IFontFamilySelectProps> = ({ value, onChange }) => (
  <FormControl size="small" fullWidth>
    <Select
      value={value || FONT_FAMILIES[0]}
      onChange={(e: SelectChangeEvent<string>) => onChange(e.target.value)}
      renderValue={selected => <span style={{ fontFamily: selected }}>{selected}</span>}
    >
      {FONT_FAMILIES.map(family => (
        <MenuItem key={family} value={family} selected={family === (value || FONT_FAMILIES[0])}>
          <span style={{ fontFamily: family }}>{family}</span>
        </MenuItem>
      ))}
    </Select>
  </FormControl>
)

// ============================================================================
// LineHeightSelect
// ============================================================================

const LINE_HEIGHTS = [1.0, 1.15, 1.25, 1.4, 1.5, 1.75, 2.0, 2.5, 3.0]

export interface ILineHeightSelectProps {
  value: number
  onChange: (value: number) => void
}

export const LineHeightSelect: React.FC<ILineHeightSelectProps> = ({ value, onChange }) => (
  <FormControl size="small" fullWidth>
    <Select<number> value={value} onChange={(e: SelectChangeEvent<number>) => onChange(Number(e.target.value))}>
      {LINE_HEIGHTS.map(lh => (
        <MenuItem key={lh} value={lh}>
          {lh}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
)

// ============================================================================
// LetterSpacingSelect
// ============================================================================

const LETTER_SPACINGS = [0, 0.5, 1, 1.5, 2, 2.5, 3, 4, 5]

export interface ILetterSpacingSelectProps {
  value: number
  onChange: (value: number) => void
}

export const LetterSpacingSelect: React.FC<ILetterSpacingSelectProps> = ({ value, onChange }) => (
  <FormControl size="small" fullWidth>
    <Select<number> value={value} onChange={(e: SelectChangeEvent<number>) => onChange(Number(e.target.value))}>
      {LETTER_SPACINGS.map(ls => (
        <MenuItem key={ls} value={ls}>
          {ls}px
        </MenuItem>
      ))}
    </Select>
  </FormControl>
)
